import { QueryClient } from "@tanstack/react-query";

import { ApiError } from "~/lib/api";

/**
 * The shared query client.
 *
 * Retry policy is the interesting part: a 4xx means the request itself was wrong
 * (bad slug, expired session) and retrying just multiplies the failure, so only
 * 5xx and transport errors are retried, with backoff.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // The feed changes on an editorial cadence, not a live one. A minute of
      // freshness avoids a refetch every time the user navigates back to it.
      staleTime: 60_000,
      gcTime: 30 * 60_000,
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status < 500) return false;
        return failureCount < 3;
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 15_000),
      // A phone WebView is backgrounded constantly; refetching on every resume is
      // noise. Mounting a screen already triggers a fetch when data is stale.
      refetchOnWindowFocus: false,
    },
  },
});

/** Query key factory, so no key string is written twice. */
export const queryKeys = {
  authConfig: ["auth", "config"] as const,
  me: ["auth", "me"] as const,
  // Anything beneath this root can contain private, identity-bound data. Clear the
  // root before an account transition so a shared device can never paint one
  // member's data for the next member, including while offline.
  userScoped: ["user"] as const,
  chatThread: (userId: string) => ["user", userId, "chat", "thread"] as const,
  // Under `userScoped` because the inbox is identity-bound: the watermark that
  // decides what is unread lives on the account, so a shared device must not paint
  // one member's reminders for the next one.
  notifications: (userId: string, locale: string) =>
    ["user", userId, "notifications", locale] as const,
  notificationsUnread: (userId: string, locale: string) =>
    ["user", userId, "notifications", locale, "unread"] as const,
  // Under `userScoped` too: the routine is a diary of when this member took a
  // supplement. `routine` is the root a tick invalidates; the list is keyed by the
  // exact window drawn, and the streak by the local day it was counted from, so a
  // screen left open past midnight asks again instead of showing yesterday's number.
  routine: (userId: string) => ["user", userId, "routine"] as const,
  routineCheckIns: (userId: string, from: string, to: string) =>
    ["user", userId, "routine", "check-ins", from, to] as const,
  routineStreak: (userId: string, today: string) =>
    ["user", userId, "routine", "streak", today] as const,
  membershipInvite: (token: string) => ["membership-invite", token] as const,
  // Under `userScoped`: a booking and a question are the member's own.
  myEvents: (userId: string, locale: string) =>
    ["user", userId, "events", "mine", locale] as const,
  eventQuestions: (userId: string, slug: string) =>
    ["user", userId, "events", "questions", slug] as const,
  // Everything the member saved, and where they stopped. `saved` is the root a heart
  // invalidates; the list is keyed by locale because it resolves per translation.
  saved: (userId: string) => ["user", userId, "saved"] as const,
  savedSlugs: (userId: string) => ["user", userId, "saved", "slugs"] as const,
  savedList: (userId: string, locale: string) =>
    ["user", userId, "saved", "list", locale] as const,
  progress: (userId: string) => ["user", userId, "progress"] as const,
  articleProgress: (userId: string, slug: string) =>
    ["user", userId, "progress", "article", slug] as const,
  continueList: (userId: string, locale: string) =>
    ["user", userId, "progress", "continue", locale] as const,
  // Public, token- or slug-bound: nothing identity-shaped is cached under these.
  publicEvent: (locale: string, slug: string) =>
    ["public-event", locale, slug] as const,
  publicRegistration: (token: string) =>
    ["public-registration", token] as const,
  // Deliberately not keyed by locale: the store is Czech-only and the response does
  // not vary by app language, so a locale in the key would double-cache identical
  // data. Add it the day the offerings query passes @inContext.
  shopOfferings: ["shop", "offerings"] as const,
  news: (locale: string) => ["news", locale] as const,
  article: (locale: string, slug: string) => ["news", locale, slug] as const,
  // The protocols library reads the same endpoint as the dashboard but asks a
  // different question of it, so it gets its own namespace rather than a variant of
  // `news`: one caches three articles, the other a filtered, paged list.
  //
  // The discriminators are underscored because a topic and a format are both slugs —
  // lowercase, digits and hyphens only — so neither can be mistaken for one of them.
  //
  // Both facets are in the key: they compose on the server, so topic+format is a
  // distinct result set and not something the client can assemble from two caches.
  protocols: (locale: string, topic: string | null, format: string | null) =>
    ["protocols", locale, topic ?? "__all__", format ?? "__all__"] as const,
  protocolTopics: (locale: string) =>
    ["protocols", locale, "__topics__"] as const,
  protocolFormats: (locale: string) =>
    ["protocols", locale, "__formats__"] as const,
  // Keyed by the month being viewed rather than by the raw window, so paging back and
  // forth through the calendar reuses the cache instead of refetching.
  events: (locale: string, month: string) => ["events", locale, month] as const,
  upcomingEvent: (locale: string) => ["events", locale, "upcoming"] as const,
  // Under `userScoped`, unlike the calendar rows: the detail carries the member's own
  // booking, so a shared device must not paint one member's place for the next.
  event: (userId: string, locale: string, slug: string) =>
    ["user", userId, "events", "detail", locale, slug] as const,
  dashboardHighlight: (locale: string) =>
    ["dashboard-highlight", locale, "current"] as const,
  dashboardHighlightDetail: (locale: string, slug: string) =>
    ["dashboard-highlight", locale, "detail", slug] as const,
};
